/* File: frontend/src/components/dashboard/StatsWidget.js */
/* Version: 6.5 (UI REPAIR - Font & Spacing Fix) */
/* Date: 2025-12-10 14:15:00 EST */
/* Description: 
 * - FIXED: Explicitly enforces system sans-serif fonts to kill Times New Roman bug.
 * - LAYOUT: Increased gaps (24px) for cleaner "Platinum" look.
 * - MOBILE: Optimized swipe container width and padding.
 * - NAV: Uses useNavigate (Router v6 compatible).
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom'; 
import { 
    Briefcase, Users, FileText, Activity, 
    Shield, DollarSign, Clock, Zap, Trophy 
} from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    ink:         'var(--ink, #1e293b)',  // класс A
    muted:       'var(--ink-muted, #64748b)',  // класс A
    surfaceSoft: 'var(--surface-soft, #f1f5f9)',  // класс A
    amber5:      'var(--amber-accent, #ca8a04)',  // класс B
    amber6:      'var(--amber-accent, #ea580c)',  // класс B
    blue2:       'var(--blue-accent, #2563eb)',  // класс B
    green2:      'var(--green-accent, #16a34a)',  // класс B
    purple1:     'var(--purple-accent, #9333ea)',  // класс B
    red1:        'var(--red-accent, #dc2626)',  // класс B
    teal3:       'var(--green-accent, #0d9488)',  // класс B
    chipAmber2:  'var(--chip-amber-bg, #fef9c3)',  // класс C
    chipAmber3:  'var(--chip-amber-bg, #ffedd5)',  // класс C
    chipBlue2:   'var(--chip-blue-bg, #dbeafe)',  // класс C
    chipGreen2:  'var(--chip-green-bg, #dcfce7)',  // класс C
    chipPurple1: 'var(--chip-purple-bg, #f3e8ff)',  // класс C
    chipRed1:    'var(--chip-red-bg, #fee2e2)',  // класс C
    chipTeal2:   'var(--chip-green-bg, #ccfbf1)',  // класс C
};




const ICON_MAP = {
    'briefcase': Briefcase,
    'users': Users,
    'file-text': FileText,
    'shield': Shield,
    'dollar-sign': DollarSign,
    'clock': Clock,
    'activity': Activity,
    'zap': Zap,
    'trophy': Trophy, 
    'default': Activity
};

const StatsWidget = ({ stats }) => {
    // Navigation Logic (v6)
    const navigate = useNavigate();

    const handleNav = (link) => {
        if (!link) return;
        navigate(link);
    };

    if (!stats || stats.length === 0) return null;

    return (
        <div className="stats-container-responsive" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }}>
            {/* CSS MAGIC: Grid on Desktop, Swipe on Mobile */}
            <style>{`
                .stats-container-responsive {
                    display: grid;
                    grid-template-columns: repeat(5, 1fr);
                    gap: 24px; /* INCREASED GAP for breathing room */
                    width: 100%;
                    margin-bottom: 24px; /* Safety margin bottom */
                }
                /* MOBILE OVERRIDE (iPhone/Android) */
                @media (max-width: 1024px) {
                    .stats-container-responsive {
                        display: flex;
                        overflow-x: auto;
                        scroll-snap-type: x mandatory;
                        padding-bottom: 5px; 
                        -webkit-overflow-scrolling: touch; /* iOS momentum */
                        gap: 16px;
                        margin-right: -1.5rem; /* Break container bounds */
                        padding-right: 1.5rem;
                        scrollbar-width: none;
                    }
                    .stats-container-responsive::-webkit-scrollbar { display: none; }
                    
                    /* Card Wrapper for Swipe */
                    .stats-card-wrapper {
                        min-width: 220px; /* Wide enough for horizontal layout */
                        scroll-snap-align: start;
                        height: 100%;
                    }
                }
            `}</style>

            {stats.map((stat, idx) => {
                const IconComponent = ICON_MAP[stat.icon] || ICON_MAP.default;
                
                // --- COLOR LOGIC ---
                let iconBg = P.surfaceSoft; 
                let iconColor = P.muted;
                let trendColor = P.muted;

                switch (stat.trendColor) {
                    case 'green': iconBg = P.chipGreen2; iconColor = P.green2; trendColor = P.green2; break;
                    case 'orange': iconBg = P.chipAmber3; iconColor = P.amber6; trendColor = P.amber6; break;
                    case 'purple': iconBg = P.chipPurple1; iconColor = P.purple1; trendColor = P.purple1; break;
                    case 'blue': iconBg = P.chipBlue2; iconColor = P.blue2; trendColor = P.blue2; break;
                    case 'teal': iconBg = P.chipTeal2; iconColor = P.teal3; trendColor = P.teal3; break;
                    case 'red': iconBg = P.chipRed1; iconColor = P.red1; trendColor = P.red1; break;
                    case 'gold': iconBg = P.chipAmber2; iconColor = P.amber5; trendColor = P.amber5; break;
                    default: break;
                }

                return (
                    <div 
                        key={idx} 
                        className="stats-card-wrapper" // Wrapper for Mobile Swipe
                        onClick={() => handleNav(stat.link)}
                        style={{ cursor: stat.link ? 'pointer' : 'default', height: '100%' }}
                    >
                        <div className="stats-card-modern" style={{
                            background: 'white',
                            padding: '16px',
                            borderRadius: '16px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                            border: stat.trendColor === 'teal' ? `1px solid ${P.teal3}` : `1px solid ${P.surfaceSoft}`,
                            
                            /* HORIZONTAL LAYOUT (Pro View) */
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '16px',
                            height: '100%',
                            transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => { if(window.innerWidth > 1024) e.currentTarget.style.transform = 'translateY(-3px)'; }}
                        onMouseLeave={(e) => { if(window.innerWidth > 1024) e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            {/* 1. ICON (Left) */}
                            <div className="stat-icon-box" style={{
                                backgroundColor: iconBg,
                                color: iconColor,
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <IconComponent size={24} />
                            </div>
                            
                            {/* 2. TEXT (Right) */}
                            <div className="stat-body" style={{ display: 'flex', flexDirection: 'column' }}>
                                <div className="stat-label" style={{ 
                                    fontSize: '0.75rem', 
                                    fontWeight: '600', 
                                    color: P.muted,
                                    textTransform: 'uppercase',
                                    marginBottom: '2px',
                                    letterSpacing: '0.5px'
                                }}>
                                    {stat.label}
                                </div>

                                <div className="stat-value" style={{ 
                                    fontSize: '1.5rem', 
                                    fontWeight: '800', 
                                    color: P.ink,
                                    lineHeight: '1.1'
                                }}>
                                    {stat.value}
                                </div>

                                {/* TREND (Colored Text) */}
                                {stat.trend && (
                                    <span style={{ 
                                        fontSize: '0.7rem', 
                                        fontWeight: '700', 
                                        color: trendColor,
                                        textTransform: 'uppercase',
                                        marginTop: '4px'
                                    }}>
                                        {stat.trend}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

StatsWidget.propTypes = {
    stats: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        trend: PropTypes.string,
        trendColor: PropTypes.string,
        icon: PropTypes.string,
    })).isRequired,
};

export default StatsWidget;